import useSWR from 'swr';
import type { EmissionsHotelType } from '@/server/cache/types';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useInitialParams } from '../InitialParamsProvider';
import { BASE_PATH } from '../../search/utils/path';
import { useFetchHotelStatic } from './useFetchHotelStatic';
import { getLoggingInfo } from '../lib/loggingInfo';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';

export function useHotelEmissions() {
  const initialParams = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const searchPageData = useSearchPageData();
  const { data: hotelStaticData } = useFetchHotelStatic();

  const emissionsFromSearch = searchPageData?.productsData?.emissionsHotelTypes?.length;
  const shouldFetchEmissions =
    !emissionsFromSearch && localization.configValuesApp.sustainabilityEnabled && hotelStaticData?.countryId;

  const key = shouldFetchEmissions ? hotelStaticData?.countryId : undefined;
  const { data } = useSWR<EmissionsHotelType[]>(
    key,
    async () =>
      fetchWithTracingAndLogging<EmissionsHotelType[]>(
        `${BASE_PATH}/api/hotel-detail/hotel-emissions?country=${hotelStaticData?.countryId}`,
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
          },
        },
        getLoggingInfo(initialParams.pageType, 'static', 'useHotelEmissions'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 1000 * 60 * 30,
    }
  );

  if (searchPageData?.productsData?.emissionsHotelTypes?.length) {
    return searchPageData.productsData.emissionsHotelTypes.find(
      (emission) => emission.stars === hotelStaticData?.rating
    );
  }

  if (!localization.configValuesApp.sustainabilityEnabled || !hotelStaticData?.countryId) {
    return undefined;
  }

  return data?.find((emission) => emission.stars === hotelStaticData?.rating);
}
